import { isNil } from "lodash";
import { type DOMOutputSpec, type NodeSpec, Node as ProseNode } from "prosemirror-model";
import { Plugin } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

import growthbook from "~/lib/ab/growthbook";
import { Extension, NodeExtension } from "~/lib/prose/extension";
import { formatTimestamp, REAL_AI_ACTION_ITEMS_FEATURE_FLAG } from "~/meetingRecorder/utils";

import styles from "./timestamp.module.css";

type OnTimestampClickHandler = (timestamp: number, recordingId?: string) => void;

// We can assume that a given class that extends NodeExtension is concrete if `name` and `node` are concrete.
// `any` is required to make a mixin.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ConcreteNodeExtension = new (...args: any[]) => NodeExtension & { name: string; node: NodeSpec };

/**
 * This is a mixin that takes a concrete `NodeExtension` subclass and modifies the NodeSpec to have timestamp-related
 * attributes.
 * @param Base - A concrete class that extends `NodeExtension`.
 * @returns A class that extends the provided class.
 */
export function withTimestampAttrs<T extends ConcreteNodeExtension>(Base: T) {
	return class Timestamped extends Base {
		_toDOMFn: (superToDOM?: (node: ProseNode) => DOMOutputSpec) => (node: ProseNode) => DOMOutputSpec = superToDOM => {
			return (node: ProseNode) => {
				const timestampedOutputSpec = {
					"data-recording-offset": node.attrs.recordingOffset,
					"data-recording": node.attrs.recording,
				};

				// If the super class doesn't already have a toDOM function, provide a basic one.
				if (!superToDOM) return ["div", timestampedOutputSpec, 0];

				const outputSpec = superToDOM(node);

				// I'm only handling plugins who specify `toDOM` output as an array.
				// It's technically possible to be a few different types.
				// See https://prosemirror.net/docs/ref/#model.DOMOutputSpec
				// -Wal
				if (!(outputSpec instanceof Array)) return outputSpec;

				// Not sure how this would be the case, but I'm accounting for it anyway. -Wal
				if (outputSpec.length === 0) return ["div", timestampedOutputSpec, 0];

				// This case is if the super class just specifies a tag and no attributes
				if (outputSpec.length === 1) return [outputSpec[0], timestampedOutputSpec];

				// The first item is always a string, the second item can be anything.
				// If it's an object, we know it's a set of attributes.
				if (!(outputSpec[1] instanceof Object)) return outputSpec;

				// Add the new DOM attributes to the existing spec
				return [
					outputSpec[0],
					{
						...outputSpec[1],
						...timestampedOutputSpec,
					},
					...outputSpec.slice(2),
				];
			};
		};

		// We want to make sure we preserve everything the super class already does, hence the "..."s
		node: NodeSpec = {
			...super.node,
			attrs: {
				...super.node.attrs,
				recordingOffset: { default: null },
				recording: { default: null },
			},
			parseDOM: [
				...(super.node.parseDOM || []),
				{
					tag: "[data-recording-offset][data-recording]",
					getAttrs: (dom: Node | string) => {
						if (!(dom instanceof Element)) return null;

						return {
							recordingOffset: dom.getAttribute("data-recording-offset") || null,
							recording: dom.getAttribute("data-recording") || null,
						};
					},
				},
			],
			toDOM: this._toDOMFn(super.node.toDOM),
		};
	};
}

export default class Timestamp extends Extension {
	onTimestampClick: OnTimestampClickHandler;

	constructor(options: { onTimestampClick: OnTimestampClickHandler }) {
		super(options);
		this.onTimestampClick = options.onTimestampClick;
	}

	get name() {
		return "timestamp";
	}

	get plugins() {
		return [
			new Plugin({
				props: {
					decorations: state => {
						const decorations: Decoration[] = [];

						state.doc.descendants((node, pos) => {
							const isFeatureOn = growthbook.isOn(REAL_AI_ACTION_ITEMS_FEATURE_FLAG);
							if (!isFeatureOn) return false;

							if (isNil(node.attrs.recordingOffset)) return false;

							const el = document.createElement("button");
							el.classList.add(styles.timestamp);
							el.textContent = formatTimestamp(node.attrs.recordingOffset, null);

							// Linked action items have clickable content leading to the original block. We don't want a button inside
							// a button.
							if (!node.attrs.originalBlockId) {
								el.classList.add(styles.clickable);
								el.onclick = () => {
									const timestamp = parseInt(node.attrs.recordingOffset);
									if (isNaN(timestamp)) return;

									this.onTimestampClick(timestamp, node.attrs.recording);
								};
							}

							// Nodes that have the right attributes are assumed to have a text (leaf) node as a child.
							// Subtract 1 so that the timestamp is placed right beside the text, instead of underneath the node
							const widgetPos = pos + node.nodeSize - 1;
							decorations.push(Decoration.widget(widgetPos, el));
							return false;
						});

						return DecorationSet.create(state.doc, decorations);
					},
				},
			}),
		];
	}
}
